/**
 * Rules about a person's set of role assignments that the roles dialog and its
 * tests both need. Pure — `today` is passed in rather than read from the clock,
 * so behaviour around a date boundary is testable.
 */

/** A role assignment, narrowed to just what these rules read. */
interface DatedAssignment {
  endDate: string | null;
}

/** True once a role's end date has passed — it belongs on the Previous tab. */
export function isPreviousAssignment(a: DatedAssignment, today: string): boolean {
  return Boolean(a.endDate && a.endDate < today);
}

/**
 * Collapse a set of assignments to at most one primary, keeping the first —
 * preferring a current role over an ended one.
 *
 * "At most one primary" is enforced in the app layer, not by a DB constraint,
 * so stored data can carry more than one (seeded rows did). Left alone, the
 * roles dialog would open holding two primaries the admin never chose and every
 * save would fail with "A person can have at most one primary role" — an edit
 * they can't complete and can't see the cause of, since the Previous tab
 * doesn't render the primary radio at all. Normalizing when the dialog seeds
 * means it always opens in a state the server accepts; choosing a different
 * primary then clears the rest as usual.
 */
export function enforceSinglePrimary<T extends DatedAssignment & { isPrimary: boolean }>(
  rows: T[],
  today: string,
): T[] {
  const primaries = rows.filter((r) => r.isPrimary);
  if (primaries.length <= 1) return rows;
  const keep = primaries.find((r) => !isPreviousAssignment(r, today)) ?? primaries[0];
  return rows.map((r) => (r.isPrimary === (r === keep) ? r : { ...r, isPrimary: r === keep }));
}
